import Dialog from "../../General/ConfirmableDialog";
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { formatInChicago, parseDateUtcIfNoZone } from "../../../utils/timezone";

interface IncidentNotesDialogProps {
  open: boolean;
  onClose: () => void;
  incidentId: string;
}

interface IncidentNote {
  id: string;
  note: string;
  date: string;
}

const IncidentNotesDialog: React.FC<IncidentNotesDialogProps> = ({
  open,
  onClose,
  incidentId,
}) => {
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [noteToDelete, setNoteToDelete] = useState<IncidentNote | null>(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: notes = [], isLoading } = useQuery<IncidentNote[]>({
    queryKey: ["incidentNotes", incidentId],
    queryFn: async () => {
      const response = await axios.get(
        `${apiVariable}/getIncidentNotes/${incidentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data as IncidentNote[];
    },
    enabled: !!incidentId && !!token && open,
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      await axios.put(
        `${apiVariable}/updateIncidentNote/${incidentId}`,
        { id, note },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["incidentNotes", incidentId],
      });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      setEditingNoteId(null);
      setEditingText("");
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      await axios.delete(
        `${apiVariable}/deleteIncidentNote/${incidentId}/${noteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["incidentNotes", incidentId],
      });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async (note: string) => {
      await axios.put(`${apiVariable}/addNoteToIncident/${incidentId}`, note, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["incidentNotes", incidentId],
      });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      setNewNote("");
    },
  });

  const handleAddNote = () => {
    if (newNote.trim()) {
      addNoteMutation.mutate(newNote);
    }
  };

  const handleEditNote = (note: IncidentNote) => {
    setEditingNoteId(note.id);
    setEditingText(note.note);
  };

  const handleUpdateNote = () => {
    if (editingNoteId && editingText.trim()) {
      updateNoteMutation.mutate({ id: editingNoteId, note: editingText });
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingText("");
  };

  const handleDeleteIconClick = (note: IncidentNote) => {
    setNoteToDelete(note);
  };

  const handleConfirmDelete = () => {
    if (noteToDelete) {
      deleteNoteMutation.mutate(noteToDelete.id);
      setNoteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setNoteToDelete(null);
  };

  const sortedNotes = notes
    ? [...notes].sort(
        (a, b) =>
          parseDateUtcIfNoZone(b.date).getTime() -
          parseDateUtcIfNoZone(a.date).getTime()
      )
    : [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Incident Notes</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={8}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a new note..."
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddNote}
                disabled={!newNote.trim() || addNoteMutation.isPending}
              >
                Add Note
              </Button>
            </Box>

            {isLoading ? (
              <Typography>Loading notes...</Typography>
            ) : (
              <List>
                {sortedNotes.map((note, idx) => (
                  <Paper
                    key={note.id || note.date || idx}
                    elevation={1}
                    sx={{ mb: 1, p: 1, backgroundColor: "background.default" }}
                  >
                    <ListItem
                      sx={{ pr: 12, alignItems: "flex-start" }}
                      secondaryAction={
                        editingNoteId === note.id ? (
                          <Box>
                            <IconButton
                              edge="end"
                              aria-label="save"
                              onClick={handleUpdateNote}
                              disabled={updateNoteMutation.isPending}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              aria-label="cancel"
                              onClick={handleCancelEdit}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box>
                            <IconButton
                              edge="end"
                              aria-label="edit"
                              onClick={() => handleEditNote(note)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDeleteIconClick(note)}
                              disabled={deleteNoteMutation.isPending}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )
                      }
                    >
                      {editingNoteId === note.id ? (
                        <ListItemText
                          primary={
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              maxRows={12}
                              size="small"
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              sx={{
                                "& .MuiInputBase-input": {
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                },
                              }}
                            />
                          }
                        />
                      ) : (
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {note.note}
                            </Typography>
                          }
                          secondary={formatInChicago(note.date)}
                        />
                      )}
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!noteToDelete} onClose={handleCancelDelete}>
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this note?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>No</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            disabled={deleteNoteMutation.isPending}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncidentNotesDialog;
